import React from "react";
import PropTypes from "prop-types";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import FormControl from "@mui/material/FormControl";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { createGame, getGame } from "./gameService";
import { playersConfig } from "./constants";
import lt from "./lt";
import { ACCENT_GRADIENT } from "./theme";

function GameSetup({ onSessionStart }) {
  const [gameId, setGameId] = React.useState("");
  const [playerCount, setPlayerCount] = React.useState(50);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  const handleNewGame = async () => {
    if (!gameId) {
      setError(lt.errGameIdRequired);
      return;
    }
    const count = Number(playerCount);
    if (!count || count < 1 || count > 1000) {
      setError(lt.errPlayerCountRange);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const existing = await getGame(gameId);
      if (existing) {
        setError(lt.errGameExists(gameId));
        setLoading(false);
        return;
      }
      await createGame(gameId, count);
      onSessionStart({
        gameId: gameId.toLowerCase(),
        playerCount: count,
        initialDrawnNumbers: [],
      });
    } catch {
      setError(lt.errCreateFailed);
      setLoading(false);
    }
  };

  const handleResume = async () => {
    if (!gameId) {
      setError(lt.errGameIdRequired);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const game = await getGame(gameId);
      if (!game) {
        setError(lt.errGameNotFound(gameId));
        setLoading(false);
        return;
      }
      onSessionStart({
        gameId: game.gameId,
        playerCount: game.playerCount,
        initialDrawnNumbers: game.drawnNumbers || [],
      });
    } catch {
      setError(lt.errLoadFailed);
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleResume();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ minHeight: "100vh", px: 2 }}
    >
      <Paper sx={{ width: 420, borderRadius: "10px", overflow: "hidden" }}>
        {/* Top accent bar */}
        <Box
          sx={{
            height: 3,
            background: ACCENT_GRADIENT,
          }}
        />

        <Box sx={{ p: 4 }}>
          <Typography
            variant="h5"
            sx={{ mb: 1, textAlign: "center", letterSpacing: "0.05em" }}
          >
            {lt.appTitle}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 4, textAlign: "center" }}
          >
            {lt.subtitle}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2.5 }}>
            <TextField
              label={lt.gameIdLabel}
              value={gameId}
              onChange={(e) => {
                setGameId(e.target.value.replace(/\s/g, "").slice(0, 20));
                setError("");
              }}
              onKeyDown={handleKeyDown}
              inputProps={{ maxLength: 20 }}
              autoFocus
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <TextField
              label={lt.playerCountLabel}
              value={playerCount}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") { setPlayerCount(""); return; }
                const num = Math.min(1000, Math.max(1, Number(raw)));
                setPlayerCount(num);
              }}
              onKeyDown={(e) => ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()}
              type="number"
              inputProps={playersConfig}
              helperText={lt.playerCountHelper}
            />
          </FormControl>

          {error && (
            <Typography
              color="error"
              variant="body2"
              sx={{ mb: 2.5, textAlign: "center" }}
            >
              {error}
            </Typography>
          )}

          <Box display="flex" gap={1.5}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleNewGame}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                lt.btnNewGame
              )}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleResume}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                lt.btnResume
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

GameSetup.propTypes = {
  onSessionStart: PropTypes.func.isRequired,
};

export default GameSetup;
